/**
 * main.js
 * Main entry point for product configurator
 * 
 * Architecture:
 * - Core: State management, utilities, side management
 * - UI: Form handlers, pricing, helpers
 * - Images: Upload, processing, overlay
 * - Design: Color detection, edit mode, CSS rendering
 * - Cart: Data collection, save/load, preview
 * - Persistence: Serialization, validation, restoration
 * - Transform: Position/rotation management
 * 
 * Refactored: 2025-11-20
 * - Removed ~500 lines of dead code
 * - Consolidated state initialization
 * - Extracted side management to dedicated module
 * - Moved debug utilities to separate file
 * - Moved external app guards to separate file
 */

// Initialize external app guards (FastBundle, etc.)
import { initializeExternalAppGuards } from './product-core-external-app-guards.js';
initializeExternalAppGuards();

// ============================================
// Core Module Imports
// ============================================
import * as State from './product-core-state.js';
import './product-core-window-functions.js';
import './product-ui-button-sync-manager.js';
import './product-images-multi-image-upload.js';
import { createUploadSlot, updateMultiImageUI } from './product-images-multi-image-ui.js';

// ============================================
// UNIFIED PREVIEW SYSTEM
// ============================================
let UnifiedDesignPreview = null;
let unifiedPreviewLoadPromise = null;

// ============================================
// Feature Module Imports
// ============================================
import * as ImageUpload from './product-images-image-upload.js';
import * as ImageLoader from './product-images-image-loader.js';
import * as ImageOverlay from './product-images-image-overlay.js';
import * as FormHandlers from './product-ui-form-handlers.js';
import * as UIHelpers from './product-ui-ui-helpers.js';
import { initScrollLock } from './product-scroll-lock.js';

// ============================================
// Cart Dependencies
// ============================================
import './product-cart-cart-data-collector.js';
import './product-cart-cart-save-service.js';

// ============================================
// Customer Media Library (Fee-Free Reuse)
// ============================================
let customerMediaLibrary = null;
let customerMediaLibraryLoadPromise = null;

// ============================================
// Upload Progress Indicator
// ============================================
import { uploadProgressIndicator } from './product-ui-upload-progress-indicator.js';

// ============================================
// NEW V2 Edit Mode System (Complete Replacement)
// ============================================
let editModeOrchestrator = null;
let editModeModulesLoadPromise = null;

// ============================================
// Accurate Cart Preview System (NEW)
// ============================================
let cartPreviewLoadPromise = null;

// ============================================
// Unified Cart Save System
// ============================================
import cartSaveService from './product-cart-cart-save-service.js';
let imageUploadService = null;
let imageUploadServiceLoadPromise = null;

// ============================================
// Print Area Overlay System
// ============================================
let initializePrintAreaOverlay = null;
let printAreaOverlayLoadPromise = null;
let processUploadedFileFn = null;
let hasLoggedMissingProcessUploadedFile = false;

// ============================================
// State Initialization & Event Handling
// ============================================

/**
 * Initialize unified preview system
 */
async function initializeUnifiedPreview() {
  try {
    console.log(' UnifiedDesignPreview: Initializing...');
    
    // Initialize unified preview
    const unifiedPreviewModule = await ensureUnifiedPreviewLoaded();
    if (unifiedPreviewModule && typeof unifiedPreviewModule.initialize === 'function') {
      await unifiedPreviewModule.initialize();
      if (typeof window !== 'undefined' && !window.unifiedPreview) {
        window.unifiedPreview = unifiedPreviewModule;
      }
    } else if (typeof unifiedPreviewModule === 'function') {
      const productWrapper = document.getElementById('product-wrapper');
      if (!productWrapper) {
        console.warn('Unified preview skipped: #product-wrapper not found');
        return;
      }

      let previewContainer = document.getElementById('unified-preview-container-product');
      if (!previewContainer) {
        previewContainer = document.createElement('div');
        previewContainer.id = 'unified-preview-container-product';
        previewContainer.style.cssText = [
          'position:absolute',
          'top:0',
          'left:0',
          'width:100%',
          'height:100%',
          'pointer-events:none',
          'z-index:10'
        ].join(';');
        productWrapper.appendChild(previewContainer);
      }

      const previewInstance = new unifiedPreviewModule({
        container: previewContainer,
        mode: 'INTERACTIVE',
        productConfig: window.productConfig
      });
      await previewInstance.initialize();
      if (typeof window !== 'undefined') {
        window.unifiedPreview = previewInstance;
      }
    } else {
      throw new Error('UnifiedDesignPreview export is not callable');
    }
    
    console.log(' Unified preview system initialized for product page');

  } catch (error) {
    console.error(' Failed to initialize unified preview:', error);
    // Don't fail the entire app if preview fails
  }
}

async function ensureUnifiedPreviewLoaded() {
  if (UnifiedDesignPreview) return UnifiedDesignPreview;
  if (unifiedPreviewLoadPromise) return unifiedPreviewLoadPromise;

  unifiedPreviewLoadPromise = (async () => {
    const previewModule = await import('./product-transform-unified-design-preview.module.js');
    UnifiedDesignPreview =
      previewModule?.default ||
      previewModule?.UnifiedDesignPreview ||
      window?.UnifiedDesignPreview ||
      null;
    return UnifiedDesignPreview;
  })();

  return unifiedPreviewLoadPromise;
}

async function waitForProductConfig(timeoutMs = 5000) {
  try {
    const existing = window.productConfig;
    if (existing && Array.isArray(existing.print_areas) && existing.print_areas.length > 0) {
      return existing;
    }

    return await new Promise((resolve) => {
      let timeoutId;
      const onReady = (e) => {
        cleanup();
        resolve(e?.detail?.config || window.productConfig || null);
      };
      const cleanup = () => {
        window.removeEventListener('productConfigReady', onReady);
        if (timeoutId) clearTimeout(timeoutId);
      };

      window.addEventListener('productConfigReady', onReady, { once: true });

      timeoutId = setTimeout(() => {
        cleanup();
        resolve(window.productConfig || null);
      }, timeoutMs);
    });
  } catch (e) {
    return window.productConfig || null;
  }
}

function hasPotentialEditModeFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    let hasSession = false;
    try {
      hasSession = !!sessionStorage.getItem('edit_cart_item') || 
                   !!sessionStorage.getItem('reorder_from_order') || 
                   !!sessionStorage.getItem('revision_order_data');
    } catch (_) {}
    return (
      params.has('edit_cart_item') ||
      params.has('reorder_order_id') ||
      params.has('revision_order_id') ||
      params.has('design_id') ||
      hasSession
    );
  } catch (_) {
    return false;
  }
}

async function ensureEditModeModulesLoaded() {
  if (editModeOrchestrator) return editModeOrchestrator;
  if (editModeModulesLoadPromise) return editModeModulesLoadPromise;

  editModeModulesLoadPromise = (async () => {
    const [orchestratorModule] = await Promise.all([
      import('./product-design-edit-mode-orchestrator.js'),
      import('./product-persistence-design-restoration-engine-v2.js'),
      import('./product-ui-button-state-manager-v2.js'),
      import('./product-state-restoration-state-manager.js'),
      import('./product-cart-cart-update-manager-v2.js')
    ]);

    editModeOrchestrator =
      orchestratorModule?.default ||
      window?.ProductConfigurator?.EditModeOrchestrator ||
      null;

    return editModeOrchestrator;
  })();

  return editModeModulesLoadPromise;
}

async function ensureCustomerMediaLibraryLoaded() {
  if (customerMediaLibrary) return customerMediaLibrary;
  if (customerMediaLibraryLoadPromise) return customerMediaLibraryLoadPromise;

  customerMediaLibraryLoadPromise = (async () => {
    const mediaLibraryModule = await import('./product-customer-media-library.js');
    customerMediaLibrary =
      mediaLibraryModule?.customerMediaLibrary ||
      window?.customerMediaLibrary ||
      null;
    return customerMediaLibrary;
  })();

  return customerMediaLibraryLoadPromise;
}

async function ensureImageUploadServiceLoaded() {
  if (imageUploadService) return imageUploadService;
  if (imageUploadServiceLoadPromise) return imageUploadServiceLoadPromise;

  imageUploadServiceLoadPromise = (async () => {
    const imageUploadServiceModule = await import('./product-images-image-upload-service.js');
    imageUploadService =
      imageUploadServiceModule?.default ||
      window?.ProductConfigurator?.ImageUploadService ||
      null;
    return imageUploadService;
  })();

  return imageUploadServiceLoadPromise;
}

async function ensurePrintAreaOverlayManagerLoaded() {
  if (initializePrintAreaOverlay) return initializePrintAreaOverlay;
  if (printAreaOverlayLoadPromise) return printAreaOverlayLoadPromise;

  printAreaOverlayLoadPromise = (async () => {
    const overlayModule = await import('./product-design-print-area-overlay-manager.js');
    initializePrintAreaOverlay =
      overlayModule?.initializePrintAreaOverlay ||
      window?.initializePrintAreaOverlay ||
      null;
    return initializePrintAreaOverlay;
  })();

  return printAreaOverlayLoadPromise;
}

async function ensureAccurateCartPreviewLoaded() {
  if (cartPreviewLoadPromise) return cartPreviewLoadPromise;

  cartPreviewLoadPromise = import('./product-cart-accurate-cart-preview.js').catch((error) => {
    cartPreviewLoadPromise = null;
    throw error;
  });

  return cartPreviewLoadPromise;
}

function scheduleNonCriticalPreloads() {
  if (typeof window === 'undefined') return;

  const preload = () => {
    ensureCustomerMediaLibraryLoaded().catch(() => {});
    ensureImageUploadServiceLoaded().catch(() => {});
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(preload, { timeout: 2500 });
  } else {
    setTimeout(preload, 1200);
  }
}

function resolveProcessUploadedFileFn() {
  if (typeof processUploadedFileFn === 'function') return processUploadedFileFn;

  const candidates = [
    ImageUpload?.processUploadedFile,
    window?.ImageUploadAPI?.processUploadedFile,
    window?.ImageUpload?.processUploadedFile,
    window?.processUploadedFile
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'function') {
      processUploadedFileFn = candidate;
      return processUploadedFileFn;
    }
  }

  if (!hasLoggedMissingProcessUploadedFile) {
    hasLoggedMissingProcessUploadedFile = true;
    console.error(
      'processUploadedFile is unavailable. Upload API bindings are missing in the current bundle/runtime.'
    );
  }

  return null;
}

async function buildFileFromLibraryUrl(url, filename) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch library image (${response.status})`);
  }
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
}

async function processLibrarySelectionImage(imageData) {
  const { url, filename, side, index, isFree } = imageData;

  if (!url || !side || index === undefined || index === null) {
    console.warn('processLibraryImage received invalid payload, skipping:', imageData);
    return;
  }

  console.log(`Processing library image: ${filename} for ${side}[${index}]`);
  const progressKey = uploadProgressIndicator.show(side, index, true);
  uploadProgressIndicator.setStep(progressKey, 'fetch', 'Loading selected image...');

  try {
    const file = await buildFileFromLibraryUrl(url, filename);
    uploadProgressIndicator.setStep(progressKey, 'validate', 'Preparing your design...');

    const processUploadedFile = resolveProcessUploadedFileFn();
    if (typeof processUploadedFile === 'function') {
      // PRIMARY PATH: processUploadedFile takes isFree + libraryUrl directly
      uploadProgressIndicator.setStep(progressKey, 'process', 'Applying design to product...');
      await processUploadedFile(file, side, index, true, url);
    } else {
      // FALLBACK: proceedWithUpload — MUST forward isFree=true and url as libraryUrl
      // so the image is never charged as a new upload.
      const proceedWithUploadFallback =
        ImageUpload?.proceedWithUpload ||
        window?.ImageUploadAPI?.proceedWithUpload ||
        window?.proceedWithUpload;

      if (typeof proceedWithUploadFallback === 'function') {
        uploadProgressIndicator.setStep(progressKey, 'process', 'Applying design to product...');
        // Signature: proceedWithUpload(event, side, index, file, isFree, libraryUrl)
        await proceedWithUploadFallback(
          { target: { files: [file] } },
          side,
          Number(index) || 0,
          file,
          true,   // isFree — the core fix
          url     // libraryUrl — the core fix
        );
      } else {
        // LAST RESORT FALLBACK: handleUpload — also forwards isFree + url
        const handleUploadFallback =
          ImageUpload?.handleUpload ||
          window?.ImageUploadAPI?.handleUpload ||
          window?.handleUpload;

        if (typeof handleUploadFallback !== 'function') {
          throw new Error('Library processing unavailable (processUploadedFile/handleUpload missing)');
        }

        file.__skipNonVectorWarning = true;
        window.__skipNonVectorWarningOnce = true;
        uploadProgressIndicator.setStep(progressKey, 'process', 'Applying design to product...');
        // Signature: handleUpload(event, side, index, isFree, libraryUrl)
        await handleUploadFallback(
          { target: { files: [file] } },
          side,
          Number(index) || 0,
          true,  // isFree
          url    // libraryUrl
        );
      }
    }

    if (typeof window.updateMultiImageUI === 'function') {
      window.updateMultiImageUI(side);
    }

    // Dispatch uploadedLocationsChanged so PricingCalculator recalculates the fee.
    // The reusedImageRegistryChanged event (dispatched by the registry) also triggers
    // a recalc, but we dispatch this as a belt-and-suspenders approach and also
    // to fix the currently dead uploadedLocationsChanged listener in pricing-calculator.js.
    if (typeof document !== 'undefined') {
      try {
        const { ReusedImageRegistry } = await import('./product-core-state.js');
        document.dispatchEvent(new CustomEvent('uploadedLocationsChanged', {
          detail: { reusedCount: ReusedImageRegistry.getReusedCount() }
        }));
      } catch (_) {
        document.dispatchEvent(new CustomEvent('uploadedLocationsChanged', { detail: {} }));
      }
    }

    uploadProgressIndicator.complete(progressKey);
  } catch (error) {
    console.error('Error processing library image:', error);
    uploadProgressIndicator.updateStatus(progressKey, 'Failed to load image');
    setTimeout(() => uploadProgressIndicator.hide(progressKey), 1200);
  }
}

// ============================================
// Application Initialization
// ============================================

async function initializeApplication() {
  if (typeof window !== 'undefined' && window.location?.pathname?.includes('/cart')) {
    try {
      await ensureAccurateCartPreviewLoaded();
      console.log('Cart page detected: loaded accurate cart preview module only');
    } catch (error) {
      console.warn('Failed to load accurate cart preview module:', error);
    }
    return;
  }

  if (typeof window !== 'undefined') {
    if (window.__productConfiguratorAppInitialized) return;
    window.__productConfiguratorAppInitialized = true;
  }

  console.log(' Initializing Product Configurator...');

  // Initialize scroll lock system
  initScrollLock();

  try {
    const overlayManagerPromise = ensurePrintAreaOverlayManagerLoaded().catch((error) => {
      console.warn('Failed to load print area overlay module:', error);
      return null;
    });

    const productConfig = await waitForProductConfig(5000);

    // Step 1: Initialize state (consolidated)
    console.log(' Initializing state...');
    State.initializeState(productConfig);

    // Step 1.5: Initialize print area overlay (canvas + clip container)
    console.log('️ Initializing print area overlay manager...');
    try {
      const overlayInitializer = await overlayManagerPromise;
      if (typeof overlayInitializer === 'function') {
        overlayInitializer();
      }
    } catch (overlayError) {
      console.warn('⚠️ Failed to initialize print area overlay manager:', overlayError);
    }

    // Step 2: Check for edit mode (V2 system only)
    console.log(' Checking for edit mode...');
    let isEditModeActive = false;
    if (hasPotentialEditModeFromUrl()) {
      const orchestrator = await ensureEditModeModulesLoaded();
      if (!orchestrator || typeof orchestrator.initialize !== 'function') {
        throw new Error('EditModeOrchestrator not available - critical module missing');
      }
      await orchestrator.initialize();
      isEditModeActive = !!orchestrator?.isActive;
    } else {
      console.log('Standard product view detected - skipping edit-mode bundle load');
    }

    // Step 3: Initialize upload handlers
    console.log(' Initializing upload handlers...');
    if (typeof ImageUpload.initializeUploadHandlers === 'function') {
      ImageUpload.initializeUploadHandlers();
    }

    // Step 4: Setup observers
    console.log('️ Setting up observers...');
    if (typeof ImageOverlay.setupProductImageObserver === 'function') {
      ImageOverlay.setupProductImageObserver();
    }

    // Step 5: Setup pricing calculator
    console.log(' Setting up pricing calculator...');
    if (typeof FormHandlers.setupTotalQuantityDistribution === 'function') {
      FormHandlers.setupTotalQuantityDistribution();
    }

    // Step 7: Initialize accordions
    console.log(' Initializing accordions...');
    if (typeof UIHelpers.enableAccordions === 'function') {
      UIHelpers.enableAccordions();
    }

    // Step 8: Set initial panel/button visibility based on whether images exist
    console.log('️ Setting initial panel visibility...');
    if (typeof UIHelpers.updatePanelVisibility === 'function') {
      UIHelpers.updatePanelVisibility();
    }

    // Step 9: Initialize unified preview system (only for non-edit modes)
    console.log(' Initializing unified preview system...');
    if (!isEditModeActive) {
      await initializeUnifiedPreview();
    }

    scheduleNonCriticalPreloads();

    console.log(' Product Configurator initialized successfully!');

  } catch (error) {
    console.error(' Failed to initialize Product Configurator:', error);
    console.error('Stack trace:', error.stack);
  }
}

// ============================================
// One-time bootstrap bindings
// ============================================
// If this module is injected more than once, avoid re-binding listeners and globals.
if (typeof window === 'undefined' || !window.__productConfiguratorBootstrapBound) {
  if (typeof window !== 'undefined') {
    window.__productConfiguratorBootstrapBound = true;
  }

  // ============================================
  // Auto-initialize when DOM is ready
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApplication);
  } else {
    // DOM already loaded
    initializeApplication();
  }

  // ============================================
  // Late productConfig: re-initialize handlers
  // ============================================
  // When the backend fetch takes longer than waitForProductConfig's timeout (5s),
  // initializeApplication runs with null config and finds 0 upload inputs.
  // configurator-integration.liquid renders upload sections AFTER the backend responds,
  // so we must re-run initializeUploadHandlers once productConfig is available.
  window.addEventListener('productConfigReady', () => {
    // Short delay to let configurator-integration.liquid finish rendering the upload sections
    setTimeout(() => {
      if (typeof ImageUpload.initializeUploadHandlers === 'function') {
        const pending = document.querySelectorAll('.upload-input-pending');
        if (pending.length > 0) {
          ImageUpload.initializeUploadHandlers();
        }
      }
      // Also re-initialize state with real config if it came in late
      if (window.productConfig && typeof State.initializeState === 'function') {
        const alreadyHasAreas = Array.isArray(window.productConfig.print_areas) &&
          window.productConfig.print_areas.length > 0;
        if (alreadyHasAreas) {
          State.initializeState(window.productConfig);
        }
      }
    }, 300);
  }, { once: true });

  // ============================================
  // Legacy Support: Export key functions globally
  // ============================================
  // These are exported globally to maintain compatibility with inline event handlers
  // in product.liquid (e.g., onclick="functionName()")

  if (typeof window !== 'undefined') {
    // Upload functions (CRITICAL for image uploads to work)
    if (typeof ImageUpload.handleUpload === 'function') {
      window.handleUpload = ImageUpload.handleUpload;
    }
    if (typeof ImageUpload.removeUpload === 'function') {
      window.removeUpload = ImageUpload.removeUpload;
    }
    if (typeof ImageUpload.proceedWithUpload === 'function') {
      window.proceedWithUpload = ImageUpload.proceedWithUpload;
    }
    if (typeof ImageUpload.handleVectorFileUpload === 'function') {
      window.handleVectorFileUpload = ImageUpload.handleVectorFileUpload;
    }
    if (typeof ImageUpload.processUploadedFile === 'function') {
      window.processUploadedFile = ImageUpload.processUploadedFile;
    }

    window.ImageUploadAPI = window.ImageUploadAPI || {};
    if (typeof ImageUpload.handleUpload === 'function') {
      window.ImageUploadAPI.handleUpload = ImageUpload.handleUpload;
    }
    if (typeof ImageUpload.triggerUpload === 'function') {
      window.ImageUploadAPI.triggerUpload = ImageUpload.triggerUpload;
    }
    if (typeof ImageUpload.removeUpload === 'function') {
      window.ImageUploadAPI.removeUpload = ImageUpload.removeUpload;
    }
    if (typeof ImageUpload.proceedWithUpload === 'function') {
      window.ImageUploadAPI.proceedWithUpload = ImageUpload.proceedWithUpload;
    }
    if (typeof ImageUpload.processUploadedFile === 'function') {
      window.ImageUploadAPI.processUploadedFile = ImageUpload.processUploadedFile;
    }

    // Overlay functions (CRITICAL: processUploadedImage checks window.createOverlayLayer
    // but it was never assigned here, causing uploaded designs to be invisible on the mockup)
    window.createOverlayLayer = ImageOverlay.createOverlayLayer;
    window.removeOverlayLayer = ImageOverlay.removeOverlayLayer;
    window.setActiveOverlay = ImageOverlay.setActiveOverlay;
    window.clearAllOverlays = ImageOverlay.clearAllOverlays;

    // Image loader functions
    window.displayImageOnMockup = ImageLoader.displayImageOnMockup;

    // Upload/multi-image UI helpers
    window.updateMultiImageUI = updateMultiImageUI;
    window.createUploadSlot = createUploadSlot;
  }
} else {
  console.log('ℹ️ product-core-main bootstrap already bound, skipping duplicate bindings');
}

if (typeof window !== 'undefined') {
  
  // Enhanced triggerUpload that opens media library first
  // This allows customers to choose from their paid images (fee-free) or upload new (with fee)
  window.triggerUpload = async function(side, index = 0) {
    console.log(`️ triggerUpload called for ${side}[${index}]`);

    // Feature flag: only bypass modal when explicitly disabled.
    // Undefined should default to enabled so the media-library modal opens.
    if (window.__reuseV2 === false) {
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    }
    
    // Check if we're on mobile - use mobile upload panel instead of desktop modal
    if (window.mobileUpload && window.mobileUpload.isMobile) {
      console.log(` Mobile detected - using mobile upload panel for ${side}[${index}]`);
      window.mobileUpload.handleMobileUpload(side, null, index);
      return;
    }
    
    console.log(`️ Desktop detected - opening media library modal`);
    
    let mediaLibraryLoadIndicatorKey = null;
    const mediaLibraryLoadIndicatorTimeout = setTimeout(() => {
      mediaLibraryLoadIndicatorKey = uploadProgressIndicator.show(side, index, true);
      uploadProgressIndicator.setStep(
        mediaLibraryLoadIndicatorKey,
        'fetch',
        'Opening your media library...'
      );
    }, 180);

    let mediaLibrary = null;
    try {
      mediaLibrary = await ensureCustomerMediaLibraryLoaded();
    } finally {
      clearTimeout(mediaLibraryLoadIndicatorTimeout);
      if (mediaLibraryLoadIndicatorKey) {
        uploadProgressIndicator.hide(mediaLibraryLoadIndicatorKey);
      }
    }
    if (!mediaLibrary || typeof mediaLibrary.open !== 'function') {
      console.warn('Media library unavailable, falling back to direct upload input');
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    }

    // Desktop: Open media library with callback for when image is selected.
    // The modal's confirmSelection() already calls applySelection() (registry + tracking +
    // canonical event) before invoking this callback. We only need to run the pipeline here.
    mediaLibrary.open(side, index, (selectedImage) => {
      console.log(' Image selected from media library:', selectedImage);

      if (selectedImage && selectedImage.url) {
        if (typeof window.processLibraryImage === 'function') {
          window.processLibraryImage(selectedImage);
        }
      }
    });
  };
  
  // Original triggerUpload for direct file input (bypasses media library)
  window.triggerUploadDirect = ImageUpload.triggerUpload;
  
  // Process image selected from media library
  window.processLibraryImage = processLibrarySelectionImage;
}
